<template>
  <div class="your-details-form">
    <div v-if="savedSuccessfully === true"
         class="alert alert-success">
      <p>
        Your details were updated successfully
      </p>
    </div>

    <div v-if="savedSuccessfully === false"
         class="alert alert-danger">
      <p>
        Something went wrong whilst updating your details
      </p>
    </div>

    <div class="row">
      <div class="col-lg-8">
        <div class="your-details-section">
          <h2 v-if="!noHeadings"
              class="heading">
            Your details
          </h2>

          <div class="row">
            <div class="col-lg-4">
              <div class="form-group">
                <select v-model="formData.title"
                        class="form-control"
                        placeholder="Title">
                  <option v-for="title in titles"
                          :key="title">
                    {{ title }}
                  </option>
                </select>
              </div>
            </div>

            <div class="col-lg-4">
              <div class="form-group">
                <input v-model="formData.firstName"
                       placeholder="First name"
                       class="form-control">
              </div>
            </div>

            <div class="col-lg-4">
              <div class="form-group">
                <input v-model="formData.lastName"
                       placeholder="Last name"
                       class="form-control">
              </div>
            </div>
          </div>

          <div class="form-group">
            <div class="row">
              <div class="col-lg-12">
                <input v-model="formData.email"
                       placeholder="Email"
                       class="form-control">
              </div>
            </div>
          </div>

          <div class="form-group">
            <div class="row">
              <div class="col-lg-12">
                <input v-model="formData.phone"
                       placeholder="Phone"
                       class="form-control">
              </div>
            </div>
          </div>

          <div class="form-group">
            <div class="row">
              <div class="col-lg-12">
                <b-form-checkbox v-model="formData.marketingOptIn">
                  <slot name="marketing-label">
                    Opt in to marketing
                  </slot>
                </b-form-checkbox>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-4">
        <div class="your-details-section">
          <h2 v-if="!noHeadings"
              class="heading">
            Your address / organisation
          </h2>

          <div class="edit-table">
            <div class="edit-table__row">
              <div class="edit-table__cell">
                <div class="edit-table__cell-contents"
                     data-hj-suppress>
                  <div>
                    <address class="address-box">
                      <template v-if="!editing">
                        <template v-if="formData.address.organisation">
                          <strong>{{ formData.address.organisation }}</strong><br>
                        </template>

                        <template v-if="formData.address.line1">
                          {{ formData.address.line1 }}<br>
                        </template>

                        <template v-if="formData.address.line2">
                          {{ formData.address.line2 }}<br>
                        </template>

                        <template v-if="formData.address.line3">
                          {{ formData.address.line3 }}<br>
                        </template>

                        <template v-if="formData.address.town">
                          {{ formData.address.town }}<br>
                        </template>

                        <template v-if="formData.address.region">
                          {{ formData.address.region }}<br>
                        </template>

                        <template v-if="formData.address.postcode">
                          {{ formData.address.postcode }}<br>
                        </template>

                        <button
                          v-if="canEditAddress"
                          aria-label="Edit address"
                          class="btn btn-accent-8 btn-sm btn-rounded"
                          @click="editing = true">
                          Edit
                        </button>
                      </template>

                      <template v-else>
                        <div v-if="showAddressSearch">
                          <PafSearch
                            id="organisation"
                            v-model="searchedAddress"
                            :user-type="{}"
                            placeholder="Address"
                            help-text="Enter your postcode to update your address"
                            aria-label="Search for your organisation by postcode"
                            data-vv-name="address"
                            data-vv-value-path="value"></PafSearch>
                        </div>

                        <div v-if="showSchoolSearch">
                          <OrgSearch
                            id="organisation"
                            v-model="searchedAddress"
                            :user-type="{}"
                            placeholder="School"
                            aria-label="Search for your school by postcode"
                            data-vv-name="address"
                            data-vv-value-path="value"></OrgSearch>
                        </div>

                        <button
                          class="btn btn-accent-8 btn-sm btn-rounded"
                          aria-label="Save address information"
                          @click="saveAddress">
                          Save
                        </button>
                      </template>
                    </address>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-12">
        <div class="save-changes">
          <button
            :class="btnClasses"
            :disabled="saving"
            aria-label="Update details"
            @click="saveChanges">
            <template v-if="saving">
              <span class="visually-hidden">
                Updating your details...
              </span>
              <font-awesome-icon icon="spinner"
                                 spin></font-awesome-icon>
            </template>
            <template v-else>
              Update
            </template>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import axios from "axios";
import qs from "qs";
import PafSearch from "~/Components/Forms/AddressSearch/PafSearch.vue";
import OrgSearch from "~/Components/Forms/AddressSearch/OrgSearch.vue";
import Log from "~/Scripts/modules/log";

export default {
  components: {
    PafSearch,
    OrgSearch
  },
  props: {
    btnClass: {
      type: String,
      default: "btn-accent-6 btn-rounded"
    },
    noHeadings: {
      type: Boolean,
      default: false
    },
    addressInputMethod: {
      type: String,
      default: "school"
    },
    canEditAddress: {
      type: Boolean,
      default: false
    }
  },
  data: () => ( {
    saving: false,
    editing: false,
    formData: {
      phone: "",
      email: "",
      name: "",
      address: {},
      jobTypeId: null,
      orgTypeName: null,
      marketingOptIn: false
    },
    showSaveResult: false,
    saveError: false,
    saveMessage: "",
    validateRequired: false,
    enableValidation: false,
    savedSuccessfully: null,
    titles: ["Dr", "Miss", "Mr", "Mrs", "Ms"],
    searchedAddress: null
  } ),
  computed: {
    btnClasses () {
      return ["btn", this.btnClass];
    },
    showAddressSearch () {
      return this.addressInputMethod === "address";
    },
    showSchoolSearch () {
      return this.addressInputMethod === "school";
    }
  },
  watch: {
    editing ( val ) {
      if ( !val ) {
        this.saveChanges();
      }
    },
    savedSuccessfully ( val ) {
      if ( val !== null ) {
        window.setTimeout( () => {
          this.savedSuccessfully = null;
        }, 3000 );
      }
    }
  },
  created () {
    this.getUserDetails();
  },
  methods: {
    getUserDetails () {
      const route = "/umbraco/api/YourDetails/GetYourAccountDetails";
      this.$emit( "loading", true );
      return new Promise( ( resolve, reject ) => {
        axios
          .get( route )
          .then( response => {
            if ( response.data && response.data.data ) {
              this.formData = response.data.data;
              resolve();
            } else {
              throw Error( "Bad response" );
            }
          } )
          .catch( error => {
            Log.error( "Error getting user details", error, "YourDetailsForm" );
            reject();
          } )
          .finally( () => {
            this.$emit( "loading", false );
          } );
      } );
    },
    saveChanges () {
      const route = "/umbraco/api/YourDetails/SaveYourAccountDetails";
      this.validateRequired = true;
      this.saving = true;

      axios
        .post(
          route,
          qs.stringify( {
            __RequestVerificationToken: this.$csrfToken,
            ...this.formData
          } )
        )
        .then( response => {
          if ( response.data ) {
            this.savedSuccessfully = true;
          } else {
            throw Error( "Bad response" );
          }
        } )
        .catch( error => {
          Log.error( "Error getting user details", error, "YourDetailsForm" );
        } )
        .finally( () => {
          this.saving = false;
        } );
    },
    // Hide error/success messages
    resetError () {
      this.showSaveResult = false;
    },
    saveAddress () {
      this.formData.address = {
        orgId: this.searchedAddress?.id,
        organisation: this.searchedAddress?.orgName,
        line1: this.searchedAddress?.address1,
        line2: this.searchedAddress?.address2,
        line3: this.searchedAddress?.address3,
        town: this.searchedAddress?.town,
        region: this.searchedAddress?.region,
        postcode: this.searchedAddress?.postcode
      };

      this.editing = false;
    }
  }
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.your-details-section {
  height: 100%;
  padding-bottom: 30px;

  .heading {
    letter-spacing: normal;
    font-weight: bold;
    font-size: 16pt;
    margin-top: 0;
    margin-bottom: 30px;
  }
}

.details-incorrect {
  margin-top: 30px;

  .heading {
    letter-spacing: normal;
    font-weight: bold;
    font-size: 16pt;
    margin-top: 0;
    margin-bottom: 15px;
    color: @color-accent-2;
  }

  .icon {
    padding-left: 10px;
  }
}

.alert {
  margin-bottom: 30px;

  .alert-heading {
    margin-top: 0;
    margin-bottom: 10px;
    letter-spacing: normal;
    font-weight: bold;
    font-size: 14pt;
  }

  p {
    margin-bottom: 0;
  }
}

@media (max-width: 991px) {
  .col-lg-6:last-child .your-details-section {
    margin-top: 30px;
  }

  .details-incorrect {
    padding-top: 30px;
  }
}

.address-box {
  border: 2px solid #c0dfe0;
  padding: 15px;
  background: #fff;
  border-radius: 3px;

  /deep/ .form-group:last-child {
    margin-bottom: 0;
  }

  .btn {
    margin-top: 15px;
  }
}
</style>
